import React, { useMemo, useState } from "react";
import { Collapse, Divider, Progress } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import emptyCheckIcon from "../../../../../assets/symbols/empty-check.png";
import { CircleCheck, Loader2 } from "lucide-react";
import WebUtils from "../../../../../utils/WebUtils";
import {
  getFreeQuestionBankSkillsApi,
  getQuestionBankSkillsApi,
} from "../../../../../api/candidate";

const { Panel } = Collapse;

const LeftViewComponent = ({
  userDetail,
  jobGuideIdentifier,
  guideDetails,
  setTopicSubTopic, // Accept the setTopicSubTopic function to pass to parent
  currTopicId,
  currSubTopicId,
  handleViewJd,
  middleContentRef,
  questionBankTopics,
  questionBankSkills,
  setQuestionBankSkills,
  currentSkills,
  setCurrentSkills,
  skillRegenerateFlag,
  setSkillRegenerateFlag,
  qbSkillType,
  setQBSkillType,
  industryId,
}) => {
  const [skillLoading, setSkillLoading] = useState({});
  const [skillActiveKeys, setSkillActiveKeys] = useState("");

  const subTopics = questionBankTopics?.jobSubTopic ?? [];

  const handleChange = async (next) => {
    // Normalize previous & next to arrays
    const prevArr = Array.isArray(skillActiveKeys)
      ? skillActiveKeys
      : [skillActiveKeys].filter(Boolean);
    const nextArr = Array.isArray(next) ? next : [next].filter(Boolean);

    // Keep only numeric-ish keys
    const clean = (arr) =>
      arr.filter((k) => k !== "" && !Number.isNaN(Number(k)));

    const prevClean = clean(prevArr);
    const nextClean = clean(nextArr);

    // Find the key that was JUST opened (present in next but not in prev)
    const openedKey = nextClean.find((k) => !prevClean.includes(k));

    // Update active keys in state
    setSkillActiveKeys(next);

    // If nothing new was opened (likely a close), bail
    if (!openedKey) return;

    const subTopic = subTopics[Number(openedKey)];
    if (!subTopic) return;

    const subId = subTopic.subTopicIdentifier;

    // Already fetched? Skip.
    if (questionBankSkills[subId]?.length) return;

    try {
      setSkillLoading((prev) => ({ ...prev, [subId]: true }));
      await fetchQuestionBankSkills(subTopic);
    } catch (err) {
      console.error("Error at question bank subtopics.", err);
    } finally {
      setSkillLoading((prev) => ({ ...prev, [subId]: false }));
    }
  };

  // Calculate progress using weightage from preparation guide topics
  const { totalWeightage, completedWeightage } = useMemo(() => {
    let totalWeightage = 0;
    let completedWeightage = 0;

    guideDetails?.jobTopicList?.forEach((topic) => {
      const topicWeightFactor = topic.topicWeightage / 100; // Factor of the topic's total weightage

      topic?.jobSubTopic?.forEach((subTopic) => {
        const subTopicEffectiveWeightage =
          subTopic.subtopicWeightage * topicWeightFactor;
        totalWeightage += subTopicEffectiveWeightage; // Adjusted weightage for subtopics
        if (subTopic.completed) {
          completedWeightage += subTopicEffectiveWeightage; // Adjusted weightage for completed subtopics
        }
      });
    });

    return { totalWeightage, completedWeightage };
  }, [guideDetails]);

  const progressPercentage = Math.round(
    (completedWeightage / totalWeightage) * 100,
  );

  // Handle subtitle click
  const handleSubtitleClick = (subTopicIdentifier, topicIdentifier) => {
    // Send selected subtopic and topic to parent component
    setTopicSubTopic(subTopicIdentifier, topicIdentifier);
    if (middleContentRef.current) {
      middleContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start", // Align it to the start
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const fetchQuestionBankSkills = async (subTopic) => {
    const subId = subTopic?.subTopicIdentifier;

    // prevent refetch if already cached
    if (questionBankSkills[subId]?.length) return questionBankSkills[subId];

    const skillType = subTopic?.subTopicType;

    let url;

    if (industryId) {
      url =
        getFreeQuestionBankSkillsApi +
        `?userIdentifier=${userDetail?.userIdentifier}&jobGuideIdentifier=${jobGuideIdentifier}&skillType=${skillType}`;
    } else {
      url =
        getQuestionBankSkillsApi +
        `?userIdentifier=${userDetail?.userIdentifier}&jobGuideIdentifier=${jobGuideIdentifier}&skillType=${skillType}&organizationId=${userDetail.organizationId}`;
    }

    try {
      const result = await WebUtils.httpOperations(url, {}, "GET");

      if (
        result.data?.resultStatusInfo?.resultCode?.toLowerCase() === "success"
      ) {
        const skills = result.data?.data?.skillList || [];
        // const skills = [];
        const nonEmptySkills = skills.filter(
          (item) => item?.skillName && item.skillName.trim() !== "",
        );

        const skillGenerated = result.data?.data?.skillContentGenerated;
        if (nonEmptySkills?.length > 0) {
          setSkillRegenerateFlag((prev) => {
            return {
              skillsBeingGenerated: !skillGenerated,
              noSkillsPresent: false,
            };
          });
        } else {
          setSkillRegenerateFlag((prev) => {
            return {
              skillsBeingGenerated: !skillGenerated,
              noSkillsPresent: true,
            };
          });
        }

        setQBSkillType(subTopic?.subTopicType);

        setQuestionBankSkills((prev) => ({
          ...prev,
          [subId]: nonEmptySkills,
        }));

        return nonEmptySkills;
      } else {
        throw new Error("Error at fetching question bank skills api.");
      }
    } catch (err) {
      console.error("Error fetching skills", err);
      throw err;
    }
  };

  const handleSkillGeneration = (topic) => {
    setQBSkillType(topic?.subTopicType);
    // setSkillRegenerateFlag((prev) => {
    //   return {
    //     skillsBeingGenerated: !topic?.subTopicContentGenerated,
    //     noSkillsPresent: prev?.noSkillsPresent ?? false,
    //   };
    // });
  };

  return (
    <>
      <div className="flex left-side-container  ">
        <div className="w-full">
          {/* Progress Bar */}
          <div className="mb-3 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] shadow py-[0.9rem] px-3 driver-single-guide-progress-bar">
            <div className="text-[0.9rem] text-[#101828] font-semibold leading-5">
              Your Progress
            </div>
            <Progress
              strokeColor="#F79009"
              percent={progressPercentage}
              format={(percent) => (
                <div className="text-[#475467] font-medium">{percent}%</div>
              )}
            />
          </div>

          {/* Preparation topics and subtopics */}
          <div className="font-medium text-[.92rem] mb-1 text-[#344054]">
            Preparation topics
          </div>
          <Collapse
            defaultActiveKey={["0"]}
            className="px-[0.2rem] py-[0.25rem] sm:max-h-[40vh] max-h-[25vh] overflow-y-auto orange-scroll border rounded-xl driver-single-guide-topic-subtopic-section"
            expandIconPosition="right"
            size="small"
          >
            {guideDetails?.jobTopicList?.map((topic, index) => (
              <Panel
                header={
                  <div className="text-[0.92rem] text-[#101828] font-semibold flex items-center gap-[0.6rem] leading-[1.36rem]">
                    {topic.completed ? (
                      <CheckCircleFilled
                        style={{
                          color: "#206DC5",
                          fontSize: "18px",
                          marginLeft: "-.4rem",
                        }}
                      />
                    ) : (
                      ""
                    )}
                    {topic.jobTopicTitle}
                  </div>
                }
                key={index.toString()}
              >
                <div className=" flex flex-col gap-[0.8rem] driver-single-guide-subtopic-section">
                  {topic?.jobSubTopic?.map((subTopic) => (
                    <div
                      key={subTopic.subTopicIdentifier}
                      className={`leading-[1.2rem] cursor-pointer flex items-center gap-[0.45rem] ${
                        currSubTopicId === subTopic.subTopicIdentifier &&
                        currTopicId === topic.topicIdentifier
                          ? "text-[#F79009] font-semibold"
                          : "text-[#344054] font-medium"
                      }`}
                      onClick={() => {
                        setCurrentSkills(null); // deselect the question bank skills in the middle view
                        setSkillRegenerateFlag(null);
                        handleSubtitleClick(
                          subTopic.subTopicIdentifier,
                          topic.topicIdentifier,
                        );
                      }}
                    >
                      {subTopic.completed ? (
                        <CircleCheck color="white" fill="#F79009" size={19} />
                      ) : (
                        <>
                          {/* <CircleCheck color="white" fill="#F79009" size={18} /> */}
                          <img
                            src={emptyCheckIcon}
                            alt="checkIcon"
                            className="w-[0.86rem]"
                          />
                        </>
                      )}
                      {subTopic.subTopicTitle}
                    </div>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>

          {/* Question Bank with topics and skills */}
          {/* <Divider className="mt-5 mb-3 border-gray-300" /> */}
          <div className="font-medium text-[.92rem] mt-[1.2rem] mb-1 text-[#344054]">
            Question Bank
          </div>
          <Collapse
            className="px-[0.2rem] max-h-[25vh] overflow-y-auto orange-scroll border rounded-xl driver-single-guide-topic-subtopic-section"
            expandIconPosition="right"
            size="small"
            onChange={handleChange}
            activeKey={skillActiveKeys}
          >
            {subTopics?.length > 0 ? (
              subTopics?.map((topic, index) => {
                const subId = topic?.subTopicIdentifier;
                const skills = questionBankSkills[subId];
                const loading = !!skillLoading[subId];
                return (
                  <Panel
                    key={index.toString()}
                    header={
                      <div
                        onClick={() => handleSkillGeneration(topic)}
                        className="text-[0.92rem] text-[#101828] font-semibold flex items-center gap-[0.6rem] leading-[1.36rem]"
                      >
                        {topic.completed ? (
                          <CheckCircleFilled
                            style={{
                              color: "#206DC5",
                              fontSize: "18px",
                              marginLeft: "-.4rem",
                            }}
                          />
                        ) : null}
                        {topic.subTopicTitle}
                      </div>
                    }
                  >
                    {loading && (
                      <div className="flex gap-2 justify-center items-center">
                        <Loader2 size={17} className="animate-spin" />
                        <div className="text-gray-500 font-medium">
                          Loading skills…
                        </div>
                      </div>
                    )}

                    {!loading ? (
                      skills?.length > 0 ? (
                        <ul className=" flex flex-col gap-[0.65rem]">
                          {skills?.map((s, index) => (
                            <li
                              key={index}
                              className={`${
                                currentSkills?.skillName === s?.skillName
                                  ? "text-[#F79009] font-semibold"
                                  : "text-[#344054] font-medium"
                              } flex gap-[0.55rem] items-center`}
                              onClick={() =>
                                setCurrentSkills({
                                  ...s,
                                  skillType: topic?.subTopicType,
                                })
                              }
                            >
                              <img
                                src={emptyCheckIcon}
                                alt="checkIcon"
                                className="w-[0.86rem]"
                              />
                              <span className="text-[.92rem] leading-[1.2rem] cursor-pointer flex items-center">
                                {s.skillName}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-[#475467] font-medium text-center">
                          No skills found.
                        </div>
                      )
                    ) : (
                      <></>
                    )}
                  </Panel>
                );
              })
            ) : (
              <div className="px-3 py-2 text-[#475467] font-medium">
                No question bank found.
              </div>
            )}
          </Collapse>
        </div>
      </div>
    </>
  );
};

export default LeftViewComponent;
